var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { RenderTargetTexture as BABYLON_RenderTargetTexture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/renderTargetTexture.js';
import { IntegratedDepthRenderer } from './Framebuffer.js';
import { Constants as BABYLON_Constants } from '@inter-ikea-betrakta-babylonjs/core/Engines/constants.js';
import { CameraMode, IMatrix4, IVector3 } from '../api.js';
import { LiteRenderTarget } from './utility/LiteRenderTarget.js';
import { RENDER_LAYER_SHADOW, RenderLayerMask } from './RenderLayer.js';
import { EngineCamera } from './camera/EngineCamera.js';
import { PostProcess as BABYLON_PostProcess } from '@inter-ikea-betrakta-babylonjs/core/PostProcesses/postProcess.js';
import { Camera as BABYLON_Camera } from '@inter-ikea-betrakta-babylonjs/core/Cameras/camera.js';
import { TrackedVec3 } from '../Math.js';
import { Vector3 as BABYLON_Vector3, Matrix as BABYLON_Matrix } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import './shaders/soft_shadow_transfer_depth.fragment.js';
import './shaders/soft_shadow_blur.fragment.js';
import './shaders/soft_shadow_generate_texture.fragment.js';
import { Disposable } from '../utils/Disposable.js';
function calculate_orthographic(left, right, bottom, top, near_z, far_z) {
    const n = near_z;
    const f = far_z;
    const a = 2.0 / (right - left);
    const b = 2.0 / (top - bottom);
    const c = 2.0 / (f - n);
    const d = (f + n) / (f - n);
    const i0 = (right + left) / (right - left);
    const i1 = (top + bottom) / (top - bottom);
    const projection_matrix = IMatrix4.from_values(a, 0.0, 0.0, 0.0, 0.0, b, 0.0, 0.0, 0.0, 0.0, c, 0.0, -i0, -i1, -d, 1.0);
    return projection_matrix;
}
const TRANSFER_DEPTH_TEXTURE = 'depthTexture';
const TRANSFER_INVERSE_VIEW_PROJECTION_MATRIX = 'inverseViewProjectionMatrix';
const BLUR_DIRECTION = 'direction';
const BLUR_SOURCE_TEXTURE = 'sourceTexture';
const SHADOW_PLANE_SOURCE_TEXTURE = 'sourceTexture';
const SHADOW_PLANE_REFERENCE_HEIGHT = 'referenceHeight';
const SHADOW_PLANE_INTENSITY = 'intensity';
let SoftShadowRenderer = class SoftShadowRenderer {
    create_postprocess_render_target(resolution) {
        const render_target = new BABYLON_RenderTargetTexture('', { width: resolution, height: resolution }, this._viewer.scene, false, true, BABYLON_Constants.TEXTURETYPE_HALF_FLOAT, false, BABYLON_Constants.TEXTURE_BILINEAR_SAMPLINGMODE, false, false, false, BABYLON_Constants.TEXTUREFORMAT_RED, false, 1, undefined, false, false);
        render_target.wrapU = BABYLON_Constants.TEXTURE_CLAMP_ADDRESSMODE;
        render_target.wrapV = BABYLON_Constants.TEXTURE_CLAMP_ADDRESSMODE;
        return render_target;
    }
    constructor(viewer, resolution) {
        this._render_layer_mask = new RenderLayerMask([RENDER_LAYER_SHADOW]);
        this._reference_height = 0.0;
        this._intensity = 1.0;
        resolution = Math.floor(resolution);
        this._viewer = viewer;
        this._postprocess_camera = new BABYLON_Camera('babylon why do you need a camera for postprocessing shaders', new BABYLON_Vector3(), this._viewer.scene, false);
        this._resolution = resolution;
        this._tracked_center_position = new TrackedVec3(0, 0, 0);
        this._tracked_size = new TrackedVec3(5, 5, 5);
        this._tracked_center_position.onchange = this.mark_dirty.bind(this);
        this._tracked_size.onchange = this.mark_dirty.bind(this);
        this._render_target = new LiteRenderTarget('soft shadow RT', { width: resolution, height: resolution }, this._viewer.scene, false, true, BABYLON_Constants.TEXTURETYPE_UNDEFINED, false, undefined, true, false, false, BABYLON_Constants.TEXTUREFORMAT_UNDEFINED, false, 1, undefined, true // No color attachment
        );
        this._depth_framebuffer = new IntegratedDepthRenderer(this._viewer.engine, this._render_target, 1);
        this._camera = new EngineCamera(this._viewer);
        this._postprocess_target = this.create_postprocess_render_target(resolution);
        this._postprocess_target_backbuffer = this.create_postprocess_render_target(resolution);
        this._transfer_depth_effect = new BABYLON_PostProcess('soft shadows transfer depth', 'soft_shadow_transfer_depth', [TRANSFER_INVERSE_VIEW_PROJECTION_MATRIX], [TRANSFER_DEPTH_TEXTURE], { width: 1, height: 1 }, this._postprocess_camera);
        this._blur_effect = new BABYLON_PostProcess('soft shadows blur', 'soft_shadow_blur', [BLUR_DIRECTION], [BLUR_SOURCE_TEXTURE], { width: 1, height: 1 }, this._postprocess_camera);
        this._generate_shadow_plane_texture_effect = new BABYLON_PostProcess('soft shadows generate shadow plane texture', 'soft_shadow_generate_texture', [SHADOW_PLANE_REFERENCE_HEIGHT, SHADOW_PLANE_INTENSITY], [SHADOW_PLANE_SOURCE_TEXTURE], { width: 1, height: 1 }, this._postprocess_camera);
        this._shadow_plane_target = new BABYLON_RenderTargetTexture('soft shadow plane', { width: this._render_target.getRenderWidth(), height: this._render_target.getRenderHeight() }, this._viewer.scene, false, true, BABYLON_Constants.TEXTURETYPE_UNSIGNED_BYTE, false, BABYLON_Constants.TEXTURE_BILINEAR_SAMPLINGMODE, false, false, false, BABYLON_Constants.TEXTUREFORMAT_RGBA, false, 1, undefined, false, false);
        this.texture_id = this._viewer.asset_manager.add_texture(this._shadow_plane_target);
        this._shadow_plane_target.wrapU = BABYLON_Constants.TEXTURE_CLAMP_ADDRESSMODE;
        this._shadow_plane_target.wrapV = BABYLON_Constants.TEXTURE_CLAMP_ADDRESSMODE;
        this._shadow_plane_target.hasAlpha = true;
    }
    dispose() {
        this._depth_framebuffer.dispose();
        this._render_target.dispose();
        this._postprocess_target.dispose();
        this._postprocess_target_backbuffer.dispose();
        this._shadow_plane_target.dispose();
        this._transfer_depth_effect.dispose();
        this._blur_effect.dispose();
        this._generate_shadow_plane_texture_effect.dispose();
        this._postprocess_camera.dispose();
        this._camera.dispose();
        this._viewer.asset_manager.textures.remove(this.texture_id);
    }
    render() {
        const half_extents = IVector3.scale(this._tracked_size, 0.5);
        const projection_matrix = calculate_orthographic(-half_extents.x, half_extents.x, -half_extents.z, half_extents.z, -half_extents.y, half_extents.y);
        const camera_matrix = IMatrix4.from_values(1, 0, 0, 0, 0, 0, -1, 0, 0, -1, 0, 0, this.center.x, this.center.y, this.center.z, 1);
        const view_matrix = IMatrix4.inverse(camera_matrix);
        this._camera.set_view_matrix(view_matrix);
        this._camera.set_projection_matrix(projection_matrix, CameraMode.ORTHOGRAPHIC);
        this._viewer.scene.incrementRenderId();
        this._viewer.scene._activeCamera = this._camera.camera;
        this._viewer.scene.setTransformMatrix(this._camera.camera.getViewMatrix(), this._camera.camera.getProjectionMatrix());
        this._render_target._bindFrameBuffer();
        this._render_target.activeCamera = this._camera.camera;
        this._render_target.render_lite(this._camera, this._viewer.get_meshes_by_layer_mask(this._render_layer_mask));
        this._depth_framebuffer.prepare_depth();
        this._transfer_depth_effect.onApply = (effect) => {
            effect.setTexture(TRANSFER_DEPTH_TEXTURE, this._depth_framebuffer.get_depth_texture());
            const view_projection_matrix = IMatrix4.multiply(projection_matrix, view_matrix);
            const inverse_view_projection_matrix = IMatrix4.inverse(view_projection_matrix);
            effect.setMatrix(TRANSFER_INVERSE_VIEW_PROJECTION_MATRIX, new BABYLON_Matrix().fromIMatrix4(inverse_view_projection_matrix));
        };
        this._viewer.scene.postProcessManager.directRender([this._transfer_depth_effect], this._postprocess_target.renderTarget, true);
        this._blur_effect.onApply = (effect) => {
            effect.setTexture(BLUR_SOURCE_TEXTURE, this._postprocess_target);
            effect.setFloat2(BLUR_DIRECTION, 1 / this._postprocess_target.getRenderWidth(), 0);
        };
        this._viewer.scene.postProcessManager.directRender([this._blur_effect], this._postprocess_target_backbuffer.renderTarget, true);
        this._blur_effect.onApply = (effect) => {
            effect.setTexture(BLUR_SOURCE_TEXTURE, this._postprocess_target_backbuffer);
            effect.setFloat2(BLUR_DIRECTION, 0, 1.0 / this._postprocess_target_backbuffer.getRenderHeight());
        };
        this._viewer.scene.postProcessManager.directRender([this._blur_effect], this._postprocess_target.renderTarget, true);
        this.update_shadow_plane_texture();
    }
    update_shadow_plane_texture() {
        this._generate_shadow_plane_texture_effect.onApply = (effect) => {
            effect.setTexture(SHADOW_PLANE_SOURCE_TEXTURE, this._postprocess_target);
            effect.setFloat(SHADOW_PLANE_REFERENCE_HEIGHT, this._reference_height);
            effect.setFloat(SHADOW_PLANE_INTENSITY, this._intensity);
        };
        this._viewer.scene.postProcessManager.directRender([this._generate_shadow_plane_texture_effect], this._shadow_plane_target.renderTarget, true);
    }
    get center() {
        return this._tracked_center_position;
    }
    set center(center) {
        this._tracked_center_position.set(center.x, center.y, center.z);
    }
    get size() {
        return this._tracked_size;
    }
    set size(size) {
        this._tracked_size.set(size.x, size.y, size.z);
    }
    get reference_height() {
        return this._reference_height;
    }
    set reference_height(reference_height) {
        if (reference_height !== this._reference_height)
            this.mark_dirty();
        this._reference_height = reference_height;
    }
    get intensity() {
        return this._intensity;
    }
    set intensity(intensity) {
        if (intensity !== this._intensity)
            this.mark_dirty();
        this._intensity = intensity;
    }
    get render_layer_mask() {
        return this._render_layer_mask;
    }
    set render_layer_mask(mask) {
        if (this._render_layer_mask.bitmap !== mask.bitmap)
            this.mark_dirty();
        this._render_layer_mask = mask;
    }
    get resolution() {
        return this._resolution;
    }
    set resolution(resolution) {
        if (this._resolution === resolution)
            return;
        this._resolution = resolution;
        this._render_target.resize(resolution);
        this._postprocess_target.resize(resolution);
        this._postprocess_target_backbuffer.resize(resolution);
        this._shadow_plane_target.resize(resolution);
        this._depth_framebuffer.resize(resolution, resolution);
        this.mark_dirty();
    }
    mark_dirty() {
        this._viewer.mark_scene_dirty();
    }
};
SoftShadowRenderer = __decorate([
    Disposable
], SoftShadowRenderer);
export { SoftShadowRenderer };
//# sourceMappingURL=SoftShadowRenderer.js.map